import type { ReactNode } from "react";
import { Popover } from "radix-ui";
import { CircleHelp } from "lucide-react";

import { cn } from "@/lib/cn";

export interface HelpTipProps {
  /** What the thing is, in two or three sentences a first-time reader needs. */
  children: ReactNode;
  /** Names the thing for a screen reader: "What is Cover, two weeks?" */
  subject?: string;
  className?: string;
}

/**
 * A small question mark that explains the thing it sits beside.
 *
 * The client's first note on the dashboard was "explain the table", and the
 * honest answer is that nothing on a screen should need a phone call to be
 * understood. A description under a title carries one line; this carries the
 * paragraph, on demand, without spending the space on every visit. It is a
 * real button, so a keyboard reaches it, and the panel is a popover rather
 * than a hover tip, so a finger reaches it too.
 */
export function HelpTip({ children, subject, className }: HelpTipProps) {
  const label = subject ? `What is ${subject}?` : "What is this?";
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          title={label}
          data-print="hide"
          className={cn(
            "inline-flex size-6 shrink-0 items-center justify-center rounded-full text-fg-placeholder",
            "transition-colors hover:bg-ink-100 hover:text-fg-muted",
            "data-[state=open]:bg-brand-50 data-[state=open]:text-accent",
            className,
          )}
        >
          <CircleHelp className="size-4" aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            "z-50 w-72 max-w-[calc(100vw-1.5rem)] rounded-md bg-surface-raised p-4 shadow-raised ring-1 ring-inset ring-line",
            "text-base leading-relaxed text-fg-muted",
          )}
        >
          {subject ? <p className="mb-1 text-md font-semibold text-fg">{subject}</p> : null}
          <div className="space-y-2">{children}</div>
          <Popover.Arrow className="fill-surface-raised" width={12} height={6} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
